/**
 * SRAC Membership Card Modal
 *
 * Shows a member their card full-screen, without a dedicated page. Opened from
 * the profile menu and from the membership page, so the card is always one
 * click away — it carries the member number and the current field gate
 * combination, which is why the welcome email links here rather than inlining a
 * code that goes stale the moment the lock changes.
 *
 * A membership may cover a dependent (spouse or junior) as well as the primary
 * member; both cards are shown and swiped between, with dots and arrow keys for
 * anyone not on a touch screen. The relationship is exactly one deep —
 * `secondaryMembershipId` on the primary, `primaryMembershipId` on the
 * dependent — so this is always a small, finite set rather than a list needing
 * pagination.
 *
 * Card fields resolve through `getFieldValue`, and a `source: 'shared'` field
 * such as the gate code comes from `membershipInfo` — which must be read via
 * `membershipInfoQuery`, since that collection no longer grants an
 * unconditional list and an over-broad query is rejected outright.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { doc, getDoc, getDocs } from 'firebase/firestore';
import { ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { useFirestore } from '@/firebase';
import { membershipInfoQuery } from '@/lib/membership-info-access';
import MembershipCardPreview from '@/components/features/membership-card-preview';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import type { MembershipCardConfig } from '@/models/membership-card-config';
import type { Membership } from '@/models/membership';
import type { Profile } from '@/models/profile';

/** One card the modal can show. */
export interface CardHolder {
  /** The membership the card is issued against. */
  membership: Membership;
  /** The person named on it. */
  profile: Profile;
  /** Whether this is the primary member rather than a dependent. */
  isPrimary: boolean;
}

/** Props controlling the modal and what it shows. */
export interface MemberCardModalProps {
  /** Whether the modal is open. */
  open: boolean;
  /** Called when the member closes it. */
  onOpenChange: (open: boolean) => void;
  /** The member and any dependent, primary first. */
  holders: CardHolder[];
  /** Signed-in user, for `source: 'user'` card fields. */
  user?: {
    displayName?: string | null;
    email?: string | null;
    photoURL?: string | null;
    phoneNumber?: string | null;
  } | null;
  /** Whether the viewer holds a membership, for the shared-field query. */
  hasMembership: boolean;
}

/** Distance in pixels a swipe must cover before it changes cards. */
const SWIPE_THRESHOLD = 48;

/**
 * Render a member's card (and any dependent's) full-screen.
 *
 * @param props - Open state, the cards to show, and viewer context.
 * @returns The modal.
 */
export default function MemberCardModal({
  open,
  onOpenChange,
  holders,
  user,
  hasMembership,
}: MemberCardModalProps) {
  const firestore = useFirestore();
  const [cardConfig, setCardConfig] = useState<MembershipCardConfig | null>(null);
  const [sharedData, setSharedData] = useState<Record<string, unknown>>({});
  const [loading, setLoading] = useState(true);
  const [index, setIndex] = useState(0);
  const touchStartX = useRef<number | null>(null);

  // Loaded on first open rather than on mount: most sessions never open the
  // card, and this is two Firestore reads.
  useEffect(() => {
    if (!open) return;
    let active = true;

    (async () => {
      try {
        const [configSnap, infoSnap] = await Promise.all([
          getDoc(doc(firestore, 'membershipCardConfig', 'default')),
          getDocs(
            membershipInfoQuery(firestore, {
              signedIn: true,
              hasMembership,
              isOfficerOrAdmin: false,
            }),
          ),
        ]);
        if (!active) return;

        if (configSnap.exists()) {
          setCardConfig(configSnap.data() as MembershipCardConfig);
        }

        const shared: Record<string, unknown> = {};
        infoSnap.forEach((infoDoc) => {
          const data = infoDoc.data() as { name?: string; value?: unknown };
          if (data.name && data.value !== undefined) shared[data.name] = data.value;
        });
        setSharedData(shared);
      } catch (error) {
        console.error('Could not load the membership card:', error);
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [firestore, hasMembership, open]);

  // Clamped rather than reset: a dependent being added or removed while the
  // modal is open must not leave the index pointing past the end.
  useEffect(() => {
    setIndex((current) => Math.min(current, Math.max(holders.length - 1, 0)));
  }, [holders.length]);

  /** Move by one card, staying within range. */
  const move = useCallback(
    (delta: number) => {
      setIndex((current) =>
        Math.min(Math.max(current + delta, 0), Math.max(holders.length - 1, 0)),
      );
    },
    [holders.length],
  );

  // Arrow keys, because swiping is not available to everyone.
  useEffect(() => {
    if (!open || holders.length < 2) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') move(-1);
      if (event.key === 'ArrowRight') move(1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [holders.length, move, open]);

  const holder = holders[index];
  const multiple = holders.length > 1;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex h-[100dvh] max-h-[100dvh] w-screen max-w-none flex-col gap-4 rounded-none p-4 sm:p-6">
        <DialogTitle className="text-center text-lg">
          {holder
            ? `${holder.profile.firstName ?? ''} ${holder.profile.lastName ?? ''}`.trim() ||
              'Membership card'
            : 'Membership card'}
        </DialogTitle>
        <DialogDescription className="text-center text-sm text-muted-foreground">
          {holder?.isPrimary === false
            ? 'Dependent membership'
            : 'Your membership card'}
        </DialogDescription>

        <div
          className="flex flex-1 items-center justify-center overflow-auto"
          onTouchStart={(e) => {
            touchStartX.current = e.touches[0]?.clientX ?? null;
          }}
          onTouchEnd={(e) => {
            const start = touchStartX.current;
            touchStartX.current = null;
            if (start === null || !multiple) return;
            const delta = (e.changedTouches[0]?.clientX ?? start) - start;
            if (Math.abs(delta) < SWIPE_THRESHOLD) return;
            move(delta < 0 ? 1 : -1);
          }}
        >
          {loading ? (
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          ) : !cardConfig || !holder ? (
            <p className="text-center text-sm text-muted-foreground">
              Your membership card is not available right now. Contact the SRAC
              Treasurer if this continues.
            </p>
          ) : (
            <div className="w-full max-w-md">
              <MembershipCardPreview
                fields={cardConfig.fields}
                template={cardConfig.cardTemplate}
                templateType="card"
                membershipData={holder.membership}
                profileData={holder.profile}
                sharedData={sharedData}
                userData={
                  user
                    ? {
                        displayName: user.displayName || '',
                        email: user.email || '',
                        photoURL: user.photoURL || '',
                        phoneNumber: user.phoneNumber || '',
                      }
                    : undefined
                }
                interactive={false}
              />
            </div>
          )}
        </div>

        {multiple && (
          <div className="flex items-center justify-center gap-4">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Previous card"
              disabled={index === 0}
              onClick={() => move(-1)}
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <div className="flex gap-2">
              {holders.map((candidate, i) => (
                <button
                  key={candidate.membership.id ?? i}
                  type="button"
                  aria-label={`Show card ${i + 1} of ${holders.length}`}
                  aria-current={i === index}
                  className={`h-2.5 w-2.5 rounded-full transition-colors ${
                    i === index ? 'bg-primary' : 'bg-muted-foreground/30'
                  }`}
                  onClick={() => setIndex(i)}
                />
              ))}
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              aria-label="Next card"
              disabled={index === holders.length - 1}
              onClick={() => move(1)}
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
